import { call, put, take } from 'redux-saga/effects';

import { type GeneratorResult, selectTyped } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import type {
    NavConfig,
    NavConfigRefocusConfigTopLevelElement,
    NavConfigRefocusTargetTopLevelElement,
    ViewName,
} from '@pressreader/navigation-types';
import { routingManager } from '@pressreader/routing';
import { ExtractReturnType } from '@pressreader/types';

import { getParams, viewer } from '../api';
import { type NavigationParams, ReplaceType } from '../types';
import { isNavigateFailedPayload, isStaleNavigation } from '../utils';

import {
    navConfigUpdated,
    navigateDone,
    type NavigateDonePayload,
    navigateFailed,
    type NavigateFailedPayload,
    navigateInterrupted,
    type NavigateInterruptedPayload,
    pushStateToHistoryAction,
    replaceStateWithReloadHistoryAction,
    updateStateHistoryAction,
} from './actions';
import { selectCurrentNavId, selectIsNavigationInProgress } from './selectors';

function* goTo(
    params: NavigationParams,
    replaceType: ReplaceType,
): GeneratorResult<{
    isSuccess: boolean;
}> {
    const currentView = viewer?.getActiveView();

    if (currentView && currentView.canLeaveView) {
        try {
            const canLeave: ExtractReturnType<typeof currentView.canLeaveView> = yield call(currentView.canLeaveView, params);

            if (!canLeave) {
                return { isSuccess: false };
            }
        } catch (err) {
            error(err);
        }
    }

    return yield* replaceView(params, replaceType);
}

function* replaceView(params: NavigationParams, replaceType: ReplaceType): GeneratorResult<{ isSuccess: boolean }> {
    let data: ExtractReturnType<typeof routingManager.generateUrl>;
    try {
        data = yield call(routingManager.generateUrl, params);
    } catch (err) {
        error(err);
        return { isSuccess: false };
    }
    let replace = false;

    if (!data) {
        return { isSuccess: false };
    }

    const viewName = params.viewName;

    if (replaceType === ReplaceType.ForceOn) {
        replace = true;
    }

    if (replaceType === ReplaceType.On) {
        replace = viewName === viewer?.getActiveViewName();
    }

    yield call(createState, viewName, params, replace, data.url);
    return { isSuccess: true };
}

function* createState(viewName: ViewName, params: Record<string, unknown>, isReplace: boolean, to: string) {
    const allParams = getParams(viewName, params);
    const state = { ...allParams.stateParams };

    if (isReplace) {
        yield put(replaceStateWithReloadHistoryAction({ to, state }));
    } else {
        yield put(pushStateToHistoryAction({ to, state }));
    }
}

const getRefocusTargetTopLevelElement = (): NavConfigRefocusTargetTopLevelElement => ({
    type: 'topLevelElement',
    timestampMs: Number.POSITIVE_INFINITY,
    refocusTarget: {
        id: 'topLevelElement',
    },
});

const getRefocusConfigurationTopLevelElement = (): NavConfigRefocusConfigTopLevelElement => ({
    id: 'topLevelElement',
    type: 'topLevelElement',
    refocusTarget: {
        id: 'topLevelElement',
    },
    priority: 0,
});

function* waitNavigationInProgressFinish(checkNavConfig: (navConfig: NavConfig) => boolean): GeneratorResult<
    | {
          navigateAction: NavigateDonePayload | NavigateFailedPayload | NavigateInterruptedPayload;
          notInProgress: false;
          failed: boolean;
      }
    | { navigateAction: null; notInProgress: true; failed: boolean }
> {
    const isNavigationInProgress = yield* selectTyped(selectIsNavigationInProgress);

    if (!isNavigationInProgress) {
        return { navigateAction: null, notInProgress: true, failed: false };
    }
    // `navConfigUpdated` is in the list because a skip-navigation config can supersede the
    // in-flight navigation (popup open/close, history-back to a cached page) — the reducer
    // closes `isNavigationInProgress` there and the superseded navigation's own terminal
    // action never dispatches, so a waiter listening only for the terminal triple would stay
    // parked until an unrelated later navigation settles. Its payload carries no `config`,
    // so the recursion below just re-checks the in-progress flag and returns.
    const action = yield take([navigateDone, navigateFailed, navigateInterrupted, navConfigUpdated]);
    const { payload } = action;

    if (!!payload?.config && checkNavConfig(payload.config)) {
        return {
            navigateAction: payload,
            notInProgress: false,
            failed: isNavigateFailedPayload(payload),
        };
    }
    return yield* waitNavigationInProgressFinish(checkNavConfig);
}

/**
 * Persist `config` to browser history via `updateStateHistoryAction`, skipping the write when
 * this navigation has been superseded by a newer one — a stale worker must not touch history,
 * as history writes are not undoable. Staleness is judged by `isStaleNavigation` against the
 * current nav id, which reports stale ONLY when both ids are present and differ; a config or
 * current id that is absent therefore falls through and writes (its "id-less configs pass
 * through" contract). Use this in place of an unconditional `put(updateStateHistoryAction(...))`
 * in any view whose `onNavigateBegan` runs under `takeEvery` (so a stale worker survives
 * supersession) and writes history after an async suspension.
 */
function* updateStateHistoryIfCurrent(config: NavConfig, options?: { reload?: boolean; url?: string }): GeneratorResult<void> {
    const currentNavId = yield* selectTyped(selectCurrentNavId);
    if (!isStaleNavigation(config, currentNavId)) {
        yield put(updateStateHistoryAction({ params: config, ...options }));
    }
}

export { getRefocusConfigurationTopLevelElement, getRefocusTargetTopLevelElement, goTo, updateStateHistoryIfCurrent, waitNavigationInProgressFinish };
